import { useState, useCallback, useEffect } from 'react';

// Generic CRUD hook backed by localStorage with mock data as initial seed
function useAdminData<T extends { id: number | string }>(key: string, initialData: T[]) {
  const [data, setData] = useState<T[]>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) return JSON.parse(stored);
    } catch {
      // ignore parse errors
    }
    return initialData;
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      // ignore storage errors
    }
  }, [key, data]);

  const add = useCallback((item: Omit<T, 'id'> & { id?: number | string }) => {
    const newId = typeof data[data.length - 1]?.id === 'number'
      ? (data[data.length - 1].id as number) + 1
      : Date.now();
    const newItem = { ...item, id: newId } as T;
    setData((prev) => [...prev, newItem]);
    setHasChanges(true);
  }, [data]);

  const update = useCallback((id: number | string, updates: Partial<T>) => {
    setData((prev) => prev.map((item) => (item.id === id ? { ...item, ...updates } as T : item)));
    setHasChanges(true);
  }, []);

  const remove = useCallback((id: number | string) => {
    setData((prev) => prev.filter((item) => item.id !== id));
    setHasChanges(true);
  }, []);

  const reset = useCallback(() => {
    setData(initialData);
    setHasChanges(false);
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [initialData, key]);

  return { data, setData, add, update, remove, reset, hasChanges, setHasChanges };
}

// Hook for plain array data (no ID required) backed by localStorage
export function useAdminPlainData<T>(key: string, initialData: T[]) {
  const [data, setData] = useState<T[]>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) return JSON.parse(stored);
    } catch {
      // ignore
    }
    return initialData;
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      // ignore
    }
  }, [key, data]);

  const add = useCallback((item: T) => {
    setData((prev) => [...prev, item]);
    setHasChanges(true);
  }, []);

  const update = useCallback((index: number, updates: Partial<T>) => {
    setData((prev) => prev.map((item, i) => (i === index ? { ...item, ...updates } as T : item)));
    setHasChanges(true);
  }, []);

  const remove = useCallback((index: number) => {
    setData((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  }, []);

  const reset = useCallback(() => {
    setData(initialData);
    setHasChanges(false);
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [initialData, key]);

  return { data, setData, add, update, remove, reset, hasChanges };
}

// Hook for plain object data backed by localStorage
export function useAdminObject<T extends object>(key: string, initialData: T) {
  const [data, setData] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) return JSON.parse(stored);
    } catch {
      // ignore
    }
    return initialData;
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      // ignore
    }
  }, [key, data]);

  const update = useCallback((updates: Partial<T>) => {
    setData((prev) => ({ ...prev, ...updates } as T));
    setHasChanges(true);
  }, []);

  const reset = useCallback(() => {
    setData(initialData);
    setHasChanges(false);
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [initialData, key]);

  return { data, setData, update, reset, hasChanges };
}

// Helper to read admin data from localStorage on public pages
export function getAdminData<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return fallback;
}

export default useAdminData;